import type { ProductCardBadge } from './types';

/**
 * The catalogue flags a badge is drawn from, as any service carries them.
 *
 * All are optional and nullable because the services disagree about how they arrive:
 * UPS types them nullable, and the listings API omits them from products it has not
 * scored. No absence badges a product.
 *
 * `starBrand` keeps the catalogue's own name rather than being renamed to match the
 * customer-facing label. There is no `topBrand` attribute anywhere upstream, and a
 * reader looking for one is what this name stops.
 */
export interface ProductCardBadgeAttributes {
  /** "Bestseller" in customer-facing copy. */
  bestseller?: boolean | null;
  /** "Further reduced" in customer-facing copy. Shown in sale mode only. */
  furtherReduced?: boolean | null;
  /** "Most Loved" in customer-facing copy. */
  highlyRated?: boolean | null;
  /** "Low stock" in customer-facing copy. */
  lowStock?: boolean | null;
  /** "New to Sale" in customer-facing copy. Shown in sale mode only. */
  newToSale?: boolean | null;
  /** "Selling fast" in customer-facing copy. */
  sellingFast?: boolean | null;
  /** "Top Brand" in customer-facing copy. */
  starBrand?: boolean | null;
}

export interface ToProductCardBadgeOptions {
  /**
   * Rank the badges for a sale rather than an offer. Pass the same value the card takes
   * as its own `promoteAsSale`, from the site-wide sale or offer switch, so the badge
   * and the price agree about which one the customer is looking at.
   */
  promoteAsSale?: boolean;
  /**
   * Drop the badges that describe the partner rather than the product. Set it on a
   * listing scoped to one partner, where such a badge is on every card or none and so
   * carries nothing a customer can act on.
   *
   * Not for a surface that merely happens to draw several products from one partner: a
   * block showing several partners side by side is exactly where the badge does tell
   * them apart.
   */
  suppressPartnerBadges?: boolean;
}

type BadgeName =
  | 'bestseller'
  | 'furtherReduced'
  | 'lowStock'
  | 'mostLoved'
  | 'newToSale'
  | 'sellingFast'
  | 'topBrand';

const BADGE_RULES: Record<
  BadgeName,
  {
    applies: (attributes: ProductCardBadgeAttributes) => boolean;
    badge: ProductCardBadge;
    /** Skipped under `suppressPartnerBadges`. */
    describesThePartner?: boolean;
  }
> = {
  bestseller: {
    applies: ({ bestseller }) => Boolean(bestseller),
    badge: { icon: 'bestseller', label: 'Bestseller' },
  },
  furtherReduced: {
    applies: ({ furtherReduced }) => Boolean(furtherReduced),
    badge: { label: 'Further reduced', tone: 'sale' },
  },
  lowStock: {
    applies: ({ lowStock }) => Boolean(lowStock),
    badge: { icon: 'lowStock', label: 'Low stock', tone: 'urgent' },
  },
  mostLoved: {
    applies: ({ highlyRated }) => Boolean(highlyRated),
    badge: { icon: 'mostLoved', label: 'Most Loved' },
  },
  newToSale: {
    applies: ({ newToSale }) => Boolean(newToSale),
    badge: { label: 'New to Sale', tone: 'sale' },
  },
  sellingFast: {
    applies: ({ sellingFast }) => Boolean(sellingFast),
    badge: { icon: 'sellingFast', label: 'Selling fast', tone: 'urgent' },
  },
  topBrand: {
    applies: ({ starBrand }) => Boolean(starBrand),
    badge: { icon: 'topBrand', label: 'Top Brand' },
    describesThePartner: true,
  },
};

/*
 * Each order is a priority: the card has a single badge slot, so the first badge that
 * applies takes it. Both are provisional until the badge coverage report, so reorder
 * them here rather than adding conditions to the lookup.
 *
 * Most Loved sits above Top Brand in both. On 598 prod products Top Brand is on 16%
 * against Most Loved's 2%, and 43% of Most Loved products are also Top Brand, so the
 * other order would suppress nearly half of the Most Loved badges.
 */

/** New to Sale and Further reduced are absent: their copy refers to a sale event. */
const OFFER_ORDER: BadgeName[] = ['bestseller', 'sellingFast', 'lowStock', 'mostLoved', 'topBrand'];

const SALE_ORDER: BadgeName[] = [
  'furtherReduced',
  'newToSale',
  'bestseller',
  'sellingFast',
  'lowStock',
  'mostLoved',
  'topBrand',
];

/**
 * Resolves the one badge a card shows, so that every surface answers the question the
 * same way.
 *
 * Shared rather than written per surface because the ordering is a product decision
 * rather than a local one, and a surface that quietly reverses it shows a different
 * badge for the same product. It had been copied into six transformers before this
 * existed.
 */
export const toProductCardBadge = (
  attributes: ProductCardBadgeAttributes,
  { promoteAsSale = false, suppressPartnerBadges = false }: ToProductCardBadgeOptions = {},
): ProductCardBadge | undefined => {
  const name = (promoteAsSale ? SALE_ORDER : OFFER_ORDER).find((candidate) => {
    const { applies, describesThePartner } = BADGE_RULES[candidate];

    return !(describesThePartner && suppressPartnerBadges) && applies(attributes);
  });

  return name && BADGE_RULES[name].badge;
};
